import { EntityStatus } from "../types";
import type { Product } from "../types";
import type { ExplorerSortDirection } from "../hooks/useExplorerUrlState";

/** URL / filter value for products with no linked categories. */
export const UNCATEGORIZED_CATEGORY_ID = "__none__";

export const EXPLORER_PAGE_SIZE_CARD = 12;
export const EXPLORER_PAGE_SIZE_LIST = 20;

export interface ExplorerSortOption {
  id: string;
  label: string;
}

/** Consumer explorers show published APIs/products by default. */
export function isVisibleToExplorer(status: string): boolean {
  return status === EntityStatus.ACTIVE;
}

export function matchesExplorerSearch(
  query: string,
  fields: (string | null | undefined)[],
): boolean {
  if (!query.trim()) return true;
  const q = query.toLowerCase();
  return fields.some((f) => (f ?? "").toLowerCase().includes(q));
}

export function compareExplorerValues(
  a: string | number,
  b: string | number,
  dir: ExplorerSortDirection,
): number {
  const mult = dir === "asc" ? 1 : -1;
  if (typeof a === "number" && typeof b === "number") {
    return (a - b) * mult;
  }
  return String(a).localeCompare(String(b), undefined, {
    sensitivity: "base",
    numeric: true,
  }) * mult;
}

export function paginate<T>(items: T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
}

export function productHasUncategorizedCategories(product: Product): boolean {
  return !product.categories?.length;
}

export function productMatchesCategoryFilter(
  product: Product,
  selectedCategoryIds: string[],
): boolean {
  if (!selectedCategoryIds.length) return true;

  const productCategoryIds = new Set(
    (product.categories ?? []).map((c) => c.uuid),
  );
  const wantsUncategorized = selectedCategoryIds.includes(
    UNCATEGORIZED_CATEGORY_ID,
  );
  const wantsNamedCategory = selectedCategoryIds.some(
    (id) => id !== UNCATEGORIZED_CATEGORY_ID && productCategoryIds.has(id),
  );

  return (
    (wantsUncategorized && productHasUncategorizedCategories(product)) ||
    wantsNamedCategory
  );
}

export function primaryProductCategoryName(product: Product): string {
  return product.categories?.length
    ? product.categories[0].name
    : "Uncategorized";
}

export function collectUniqueProductTags(products: Product[]): string[] {
  const tagSet = new Set<string>();
  for (const product of products) {
    for (const tag of product.tags ?? []) {
      const trimmed = tag.trim();
      if (trimmed) tagSet.add(trimmed);
    }
  }
  return Array.from(tagSet).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" }),
  );
}

export function productMatchesTagFilter(
  product: Product,
  selectedTags: string[],
): boolean {
  if (!selectedTags.length) return true;
  const productTags = new Set(
    (product.tags ?? []).map((t) => t.trim()).filter(Boolean),
  );
  return selectedTags.some((tag) => productTags.has(tag));
}

export function collectUniqueTags(
  items: { tags?: string[] | null }[],
): string[] {
  const tagSet = new Set<string>();
  for (const item of items) {
    for (const tag of item.tags ?? []) {
      const trimmed = tag.trim();
      if (trimmed) tagSet.add(trimmed);
    }
  }
  return Array.from(tagSet).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" }),
  );
}

export function matchesTagFilter(
  item: { tags?: string[] | null },
  selectedTags: string[],
): boolean {
  if (!selectedTags.length) return true;
  const productTags = new Set(
    (item.tags ?? []).map((t) => t.trim()).filter(Boolean),
  );
  return selectedTags.some((tag) => productTags.has(tag));
}
